import { format, isValid, parseISO } from "date-fns";
import { Avatar, AvatarFallback } from "@/modern/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { getAvatarColor } from "@/modern/components/user-avatar";
import type { SwivuFile } from "@/react/components/entityViews/files/types/swivuFile";
import { cn } from "@/modern/utils";

/** CompanyCam-style two-letter initials from a display name. */
export function getTwoLetterInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]![0] ?? ""}${parts[1]![0] ?? ""}`.toUpperCase();
  }
  const compact = parts[0] ?? name.trim();
  if (!compact) return "?";
  return compact.slice(0, 2).toUpperCase();
}

export function formatUploadTimeOfDay(createdAt: string | null | undefined): string | null {
  if (!createdAt) return null;
  const parsed = parseISO(createdAt);
  const date = isValid(parsed) ? parsed : new Date(createdAt);
  if (!isValid(date)) return null;
  return format(date, "h:mm a");
}

interface FileMediaUploaderMetaProps {
  file: SwivuFile;
  /** `onPhoto` — light type over image; `inline` — list row chrome. */
  variant?: "onPhoto" | "inline";
  className?: string;
}

/**
 * Media browse caption: who uploaded + time of day (filename is noise at this level).
 * Non-interactive overlay — clicks pass through to the tile for open / select.
 */
export function FileMediaUploaderMeta({
  file,
  variant = "onPhoto",
  className,
}: FileMediaUploaderMetaProps) {
  const creatorName = file.creatorName?.trim() || null;
  const initials = creatorName ? getTwoLetterInitials(creatorName) : "?";
  const timeLabel = formatUploadTimeOfDay(file.createdAt);
  const avatarColor = getAvatarColor(creatorName || "Unknown");
  const isOnPhoto = variant === "onPhoto";

  const rowClassName = cn(
    "flex min-w-0 items-center gap-2",
    isOnPhoto ? "text-white" : "text-foreground",
    className,
  );

  const row = (
    <>
      <Avatar
        className={cn(
          "shrink-0 shadow-sm",
          isOnPhoto ? "size-7 text-[0.65rem]" : "size-8 text-xs",
        )}
      >
        <AvatarFallback className={cn("font-medium tracking-wide text-white", avatarColor)}>
          {initials}
        </AvatarFallback>
      </Avatar>
      {timeLabel ? (
        <span
          className={cn(
            "min-w-0 truncate tabular-nums font-normal",
            isOnPhoto ? "text-sm text-white/95" : "text-sm text-muted-foreground",
          )}
        >
          {timeLabel}
        </span>
      ) : null}
    </>
  );

  if (!creatorName) {
    return <div className={cn(rowClassName, "pointer-events-none")}>{row}</div>;
  }

  // pointer-events-auto so tooltip can open inside a pointer-events-none gradient;
  // do not stopPropagation — tile open / select still receives the click.
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            rowClassName,
            "pointer-events-auto inline-flex max-w-full cursor-default outline-none",
          )}
        >
          {row}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">{creatorName}</TooltipContent>
    </Tooltip>
  );
}
